/**
 * Tiny purpose-built markdown → HTML renderer for assistant chat bubbles. Big libs
 * (`marked`, `markdown-it`) would add ~50 KB gzipped for a few percent of the bundle
 * when most of the value here is just paragraphs, code spans, fences, and links.
 *
 * Output is **escaped HTML** for unknown text — only the markdown affordances below
 * produce raw markup. Used with `dangerouslySetInnerHTML` (safe given the escaping).
 */

const escape = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

function inlineFormat(text: string): string {
  let out = escape(text);
  // inline code
  out = out.replace(/`([^`\n]+)`/g, (_, code) => `<code class="rounded bg-neutral-200/60 px-1 py-0.5 font-mono text-[0.85em] dark:bg-neutral-800">${code}</code>`);
  // bold
  out = out.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  // italic
  out = out.replace(/(?<![*\w])\*([^*\n]+)\*(?!\w)/g, '<em>$1</em>');
  // links [text](url)
  out = out.replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, (_, label, url) => {
    const safeUrl = String(url).replace(/"/g, '%22');
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="underline">${label}</a>`;
  });
  return out;
}

export function renderMarkdown(src: string): string {
  if (!src) return '';
  const lines = src.split(/\r?\n/);
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    // fenced code
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] ?? '';
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) {
        buf.push(lines[i]!);
        i++;
      }
      i++; // skip closing
      out.push(
        `<pre class="my-2 overflow-x-auto rounded-md bg-neutral-900 p-3 text-[12px] text-neutral-100"><code data-lang="${escape(
          lang,
        )}">${escape(buf.join('\n'))}</code></pre>`,
      );
      continue;
    }

    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i]!)) {
        items.push(`<li>${inlineFormat(lines[i]!.replace(/^\s*[-*]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ul class="my-1 ml-4 list-disc">${items.join('')}</ul>`);
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i]!)) {
        items.push(`<li>${inlineFormat(lines[i]!.replace(/^\s*\d+\.\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ol class="my-1 ml-4 list-decimal">${items.join('')}</ol>`);
      continue;
    }

    // blank line — paragraph break
    if (!line.trim()) {
      i++;
      continue;
    }

    // paragraph: gather until blank line / list / fence
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^```/.test(lines[i]!) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i]!)) {
      para.push(lines[i]!);
      i++;
    }
    out.push(`<p class="my-1 leading-relaxed">${inlineFormat(para.join(' '))}</p>`);
  }

  return out.join('');
}
